import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SystemActions } from 'Redux/thunks/systems.thunks';
import { systemsSelector } from 'Redux/selectors';

import { shouldRenderRefreshButton } from '../utils/common.utils';
import {
  defaultSortBy,
  defaultVisibleCols,
  defaultVisibleFacets,
  emptyFilterSystems,
  filterLabelMapping,
  getSystemsDefaultFilter,
} from '../constants/systemsListTableDefault';
import { StorageKeys } from '../constants/storageKeys';
import { getSystemsCurlCommand } from '../utils/systems.utils';

import { SystemsListContext, useSystemsListColumns } from '../hooks/useSystemsListColumns.hook';
import useLocalStorage from '../hooks/useLocalStorage';

import { SystemsListPage } from '../containers/SystemsListPage.container';

import FilterTable from '../components/filter-table/FilterTable';
import { ListActions } from '../components/common/ListActions.view';
import { LoadingPanel } from '../components/common/LoadingPanel';

import type { RootState } from '../types/state.types';

export const SystemsList: React.FC = () => {
  const dispatch = useDispatch();

  const systems = useSelector((state: RootState) => systemsSelector(state));
  const systemsDict = useSelector((state: RootState) => state.systems);
  const pipes = useSelector((state: RootState) => state.pipes);
  const inbounds = useSelector((state: RootState) => state.inbounds);
  const outbounds = useSelector((state: RootState) => state.outbounds);
  const transforms = useSelector((state: RootState) => state.transforms);
  const systemsLoaded = useSelector((state: RootState) => state.loadStatus.systemsLoaded);
  const subUrl = useSelector((state: RootState) => state.subscription.url);
  const token = useSelector((state: RootState) => state.subscription.token);

  const loadAllForced = () => dispatch(SystemActions.loadAllForced());

  const isLiveUpdatesEnabled = useSelector((state: RootState) => state.globals.enableLiveUpdates);
  const isLiveUpdatesSupported = useSelector(
    (state: RootState) => state.subscription.supportsLiveUpdates ?? false
  );
  const failedToConnectToLiveUpdates = useSelector(
    (state: RootState) => state.subscription.failedToConnectToLiveUpdates
  );

  const [showBytesExact, setShowBytesExact] = useLocalStorage(
    StorageKeys.dataTable,
    ['systems', 'showBytesExact'],
    false
  );

  const toggleShowBytesExact = () => {
    setShowBytesExact(!showBytesExact);
  };

  const renderActions = (selectedIds: string[], clearSelection: () => void) => {
    const selectedSystems = getSelectedSystems(selectedIds);

    const deleteBatchSystems = () => {
      dispatch(SystemActions.delete(selectedIds));
      clearSelection();
    };
    return <ListActions selection={selectedSystems} onDelete={deleteBatchSystems} name="system" />;
  };

  const handleFilterChanged = useCallback(() => {
    loadAllForced();
  }, [loadAllForced]);

  const getSelectedSystems = (selectedIds: string[]) => {
    return selectedIds.map((id) => systemsDict[id]).filter(Boolean);
  };

  const columns = useSystemsListColumns(pipes, outbounds, inbounds, transforms);

  if (systemsLoaded === false) {
    return <LoadingPanel loadingMessage="Loading systems..." />;
  }

  return (
    <SystemsListContext.Provider value={{ showBytesExact, toggleShowBytesExact }}>
      <SystemsListPage
        includeInternal={false}
        shouldRenderRefreshButton={shouldRenderRefreshButton({
          isLiveUpdatesSupported,
          isLiveUpdatesEnabled,
          failedToConnectToLiveUpdates,
        })}
      >
        <FilterTable
          actionsRenderer={renderActions}
          defaults={{
            filter: getSystemsDefaultFilter('user'),
            sortBy: defaultSortBy,
            visibleCols: defaultVisibleCols,
            visibleFacets: defaultVisibleFacets,
          }}
          emptyFilter={emptyFilterSystems}
          filterLabelMapping={filterLabelMapping}
          columns={columns}
          itemsToFilter={systems}
          tableKey="systems"
          onFilterChanged={handleFilterChanged}
          curl={getSystemsCurlCommand(subUrl, token, false)}
        />
      </SystemsListPage>
    </SystemsListContext.Provider>
  );
};
